<template>
  <form class="share-dialog">

    <section class="share-dialog__section share-with">
      <header data-testid="share-with-header" class="share-with__header">
        <h3 class="share-dialog__header-text">
          {{ shareHeader }}
        </h3>
      </header>
      <div class="share-with__content"></div>
    </section>

    <section class="share-dialog__section select-documents">
      <header data-testid="select-documents-header">
        <h3 class="share-dialog__header-text">
          <span class="select-documents__text">{{ $t('client.share_dialog.section.select_documents') }}</span>
          <span class="select-documents__number">({{ selectedAmount }})</span>
        </h3>
        <UploadNew @onUploadedNewValidated="uploadedNewValidated"/>
      </header>

      <div class="selected-documents">
        <VcSearchPicker :placeholderBlurText="$t('client.share_dialog.search.search_placeholder')"
                        :loading="false"
                        :error="false"
                        :errorLabelText="$t('client.share_dialog.search.error_text')"
                        :noDataText="$t('client.share_dialog.search.no_data_text')"
                        :items="allTemplates"
                        :selectedItems="selectedTemplates"
                        placeholderFocus=""
                        iconType="folder"
                        @onSelectionChange="selectionChange"
                        @onSearchInput="searchInput"
                        @onScrollEnd="scrollEnd"
                        @onMenuToggled="menuToggled"
        >
          <template #customItem="data">
            <VcDocItem :title="data.item.text" :uid="data.item.value" :allowActions="false"/>
          </template>
        </VcSearchPicker>

        <AddedDocs>
          <template v-if="uploadedNewValid.length">
            <VcDocItem v-for="{ uid, name, isSignable, uploaded, corrupted, corruptedLabel } in uploadedNewValid"
                       :key="uid"
                       :title="name"
                       :uid="uid"
                       :is-signable="isSignable"
                       :uploaded="uploaded"
                       :corrupted="corrupted"
                       :corrupted-label="corruptedLabel"
                       :trim-signable="isMobile" />
          </template>
        </AddedDocs>
      </div>
    </section>

    <section class="share-dialog__send-options">
      <VcCheckbox class="share-dialog__checkbox save"
                  @change="saveToMyDocs = !saveToMyDocs"
                  :label="$t('client.share_dialog.section.save_to_my_docs_label')"
                  v-if="shouldPresentSave"
                  :checked="saveToMyDocs"
                  :disabled="!selectedDocs.length"
                  data-qa="save-to-my-docs" />
      <VcCheckbox class="share-dialog__checkbox notify"
                  @change="notifyByMail = !notifyByMail"
                  :label="$t('client.share_dialog.section.notify_by_email_label')"
                  v-if="shouldPresentNotify"
                  :checked="notifyByMail"
                  :disabled="!selectedDocs.length"
                  data-qa="notify-by-email" />
      <VcTextArea class="share-dialog__textarea"
                  v-if="notifyByMail"
                  :value="mailContent"
                  :label="$t('client.share_dialog.section.text_area_label')"
                  :placeholder="$t('client.share_dialog.section.text_area_default')"
                  @input="mailContentChangeHandler" />
    </section>

    <section v-if="isStaffSelectionEnabled" class="share-dialog__section assigned-staff">
      <header data-testid="assigned-staff-header">
        <h3 class="share-dialog__header-text">
          {{ $t('client.share_dialog.section.assigned_staff') }}
        </h3>
      </header>
      <AssignStaff :staff="staff" :initialSelection="selectedStaff" @onAssignStaff="assignStaff"/>
    </section>
  </form>
</template>

<script>

import { mapGetters } from "vuex";
import AssignStaff from "../components/AssignStaff";
import AddedDocs from "../components/AddedDocs";
import UploadNew from "../components/UploadNew";
import { MAX_FILE_AMOUNT } from '@/modules/filesUploader/constants/index';
import docuformsService from "@/modules/common/services/docuformsService"

export default {
  name: 'ShareDialogContent',
  components: {
    AssignStaff,
    AddedDocs,
    UploadNew,
  },
  props: {
    isInternal: {
      type: Boolean,
      default: false,
    },
    mattersList: {
      type: Array,
      default: () => []
    },
    notifyClients: {
      type: Boolean
    },
    addToMyDocs: {
      type: Boolean
    },
  },
  data() {
    return {
      allTemplates: [],
      selectedTemplates: [],
      docs: [],
      selectedDocs: [],
      saveToMyDocs: false,
      notifyByMail: false,
      mailContent: '',
      selectedStaff: {},
      uploadedNewValid: [],
      uploadedNewInvalid: [],
    }
  },
  created() {
    this.getAllTemplates();
    this.selectedStaff = {
      text: this.account.staff.display_name,
      value: this.account.staff.uid
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapGetters("accountModule", ["account", "staffMembers"]),
    selectedAmount() {
      return this.selectedDocs.length
    },
    shouldPresentSave() {
      return true
    },
    shouldPresentNotify() {
      return true
    },
    shareHeader() {
      return this.$t(`client.share_dialog.section.${this.isInternal ? 'add_to' : 'share_with'}`)
    },
    staff() {
      return this.staffMembers.map(m => ({
          text: m.display_name,
          value: m.uid
      }))
    },
    isStaffSelectionEnabled() {
      return ['admin', 'manager'].includes(this.account.staff.role)
    }
  },
  methods: {
    mailContentChangeHandler(newVal) {
      this.mailContent = newVal;
    },
    assignStaff(member) {
      this.selectedStaff = member;
    },
    uploadedNewValidated({ validFiles, invalidFiles }) {
      this.uploadedNewValid = validFiles;
      this.uploadedNewInvalid = invalidFiles;
    },
    async getAllTemplates() {
      const res = await docuformsService.getTemplates();
      this.allTemplates = res.map(t => ({ text: t.title, value: t.uid }));
    },
    selectionChange(val) {
      this.selectedTemplates = val
    },
    searchInput(val) {
      console.log('searchInput', val)
    },
    scrollEnd(val) {
      console.log('scrollEnd', val)
    },
    menuToggled(val) {
      console.log('menuToggled', val)
    },
  },
}

</script>

<style lang="scss">
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.share-dialog {

  header {
    margin-bottom: 12px;
  }

  &__header-text {
    font-weight: 600;
    font-size: 13px;
    color: $buttonBlackTextColor;
  }

  &__upload-new-button {
    font-weight: 500;
    font-size: 13px;
    color: $blue-color-4;
    display: flex;
    align-items: center;
  }

  &__section {
    height: auto;
    margin-bottom: 26px;
  }

  .share-with {
    &__header{

    }
    &__content{
      min-height: 100px; //tmp
    }
  }

  &__header-text {
    font-weight: 600;
    font-size: 13px;
    color: $buttonBlackTextColor;
  }

  &__upload-new-button {
    font-weight: 500;
    font-size: 13px;
    color: $blue-color-4;
    display: flex;
    align-items: center;
  }

  &__section {
    height: auto;
    margin-bottom: 24px;
  }

  .share-with {
    &__header{

    }
    &__content{
      min-height: 100px; //tmp
    }
  }

  .select-documents {

    &__text {

    }
    &__number {
      margin-inline-start: 4px;
    }

    &__upload-icon {
      margin-right: 6px;
    }

    .selected-documents {
      height: auto;
      border-radius: 6px;
      background-color: $grey-color-21;
      padding: 16px;
    }
  }

  &__send-options {
    margin-bottom: 26px;
  }

  &__checkbox {
    margin-top: 0;
    margin-bottom: 28px;

    &.notify {
      margin-bottom: 14px;
    }
  }

  &__textarea {
    margin-top: 0;
    margin-bottom: 28px;
  }

  &__assigned-staff {
  }

  // remove this when background-color is implemented in component
  .v-autocomplete.v-input > .v-input__control > .v-input__slot {
    background-color: white;
  }

}



</style>
